import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketsSlaPolicyDrawerService } from '../../../services/tickets-config-drawer.services';
import { TicketSlaPolicy } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-sla-policies-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './sla-policies-list.component.html'
})
export class TicketsSlaPoliciesListComponent implements OnInit, OnDestroy {
  items: TicketSlaPolicy[] = [];
  filtered: TicketSlaPolicy[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroSoloActivas = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private drawer: TicketsSlaPolicyDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.configApi.getSlaPolicies().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar políticas SLA';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.items.filter((p) => {
      if (term && !p.name.toLowerCase().includes(term)) return false;
      if (this.filtroSoloActivas && !p.isActive) return false;
      return true;
    });
  }

  nuevo(): void { this.drawer.open(); }
  editar(item: TicketSlaPolicy): void { this.drawer.open({ id: item.id }); }
  verTargets(item: TicketSlaPolicy): void {
    this.router.navigate(['/tickets/config/sla-policies', item.id]);
  }

  toggleActiva(item: TicketSlaPolicy): void {
    this.configApi.updateSlaPolicy(item.id, {
      name: item.name,
      description: item.description ?? undefined,
      businessHours: item.businessHours,
      holidays: item.holidays,
      appliesWhen: item.appliesWhen,
      isDefault: item.isDefault,
      isActive: !item.isActive,
      sortOrder: item.sortOrder
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.cargar(),
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo actualizar' })
    });
  }

  async eliminar(item: TicketSlaPolicy): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar política SLA',
      message: `¿Eliminar la política "${item.name}"?`
    });
    if (!ok) return;
    this.configApi.deleteSlaPolicy(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.toast.deleted('Política SLA', `Se eliminó "${item.name}" correctamente.`); this.cargar(); },
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo eliminar' })
    });
  }

  volverATickets(): void { this.router.navigate(['/tickets']); }
}
